import type { ChartResult } from '../domain/types';
import { limitInterpretationLength } from './answer';
import { describeGroundedFact, selectRelevantChartFacts } from './grounding';
import { readingProfileFor } from './readingProfiles';

export interface RuleInterpretationInput {
  chart: ChartResult;
  question: string;
  previousTurn?: { question: string; answer: string };
  additionalContext?: string[];
}

type QuestionIntent = 'decision' | 'timing' | 'reason' | 'action' | 'trend' | 'focus';

const POSITIVE_MARKERS = /旺|得令|得时|相生|生扶|吉|开门|休门|生门|青龙|天乙|和合|顺/i;
const NEGATIVE_MARKERS = /衰|囚|空|破|冲|刑|害|相克|克制|凶|惊门|伤门|死门|杜门|白虎|玄武|阻|反复/i;

function detectIntent(question: string): QuestionIntent {
  if (/什么时候|多久|哪天|何时|时间|月份|日期|应期/.test(question)) return 'timing';
  if (/为什么|原因|依据|怎么判断|怎么看出/.test(question)) return 'reason';
  if (/接下来|下一步|怎么做|做什么|注意什么/.test(question)) return 'action';
  if (/变化|趋势|以后|后面|发展/.test(question)) return 'trend';
  if (/适合|能不能|可不可以|是否|会不会|行不行/.test(question)) return 'decision';
  return 'focus';
}

function chartTone(chart: ChartResult): number {
  const text = [
    chart.headline,
    ...chart.summary.flatMap((fact) => [fact.label, fact.value]),
    ...chart.sections.flatMap((section) => section.facts.flatMap((fact) => [fact.label, fact.value])),
    ...chart.warnings
  ].join('；');
  const positive = text.match(new RegExp(POSITIVE_MARKERS.source, 'gi'))?.length ?? 0;
  const negative = text.match(new RegExp(NEGATIVE_MARKERS.source, 'gi'))?.length ?? 0;
  return positive - negative;
}

function groundedFacts(chart: ChartResult, question: string): string {
  const selected = selectRelevantChartFacts(chart, question, 3);
  if (!selected.length) return `排盘标题“${chart.headline}”`;
  return selected.map(describeGroundedFact).join('，');
}

function decisionOpening(tone: number): string {
  if (tone >= 2) return '可以考虑推进，但别一次把选择定死。';
  if (tone <= -2) return '暂时不建议急着推进，先把阻力看清楚。';
  return '可以继续了解，但现在还不适合仓促定下来。';
}

/**
 * 零模型、零 API 的确定性解读。它只重组程序已经算出的字段，
 * 不伪造古籍结论，也不会把开放问题冒充成精确预测。
 */
export function buildRuleInterpretation(input: RuleInterpretationInput): string {
  const question = input.question.replace(/\s+/g, ' ').trim();
  const intent = detectIntent(question);
  const facts = groundedFacts(input.chart, question);
  const profile = readingProfileFor(input.chart.moduleId);
  const tone = chartTone(input.chart);
  const imageSummary = input.additionalContext?.[0]
    ?.replace(/^本机图像结构摘要[：:]\s*/, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 90);
  let answer: string;

  if (imageSummary) {
    answer = `本机图片分析只确认可见结构：${imageSummary}${profile.lens}，盘里能核对的是${facts}；不据此判断身份、健康或命运。`;
    return limitInterpretationLength(answer);
  }

  switch (intent) {
    case 'decision':
      answer = `${decisionOpening(tone)}${profile.lens}；盘里${facts}，当前条件${tone > 0 ? '偏主动' : tone < 0 ? '仍有牵制' : '还在变化'}。`;
      break;
    case 'timing':
      answer = `目前不能精确到具体日期。${profile.lens}；现有时间线索是${facts}，没有明确应期就不硬报哪一天。`;
      break;
    case 'reason':
      answer = `${profile.lens}；这次依据是${facts}。这些都是程序算出的字段，不把专名硬套成现实中的确定结果。`;
      break;
    case 'action':
      answer = `${tone < 0 ? '眼下先别急着拍板。' : '眼下可以推进准备，但要留出调整空间。'}${profile.lens}；重点核对${facts}。`;
      break;
    case 'trend':
      answer = `${profile.lens}；后面的变化重点看${facts}。目前过程${tone > 0 ? '有推进空间' : tone < 0 ? '容易受到牵制' : '还没有形成单一方向'}。`;
      break;
    default:
      answer = `${profile.lens}；这次最值得看的是${facts}。这些是当前盘面的核心结构。`;
  }

  const limited = limitInterpretationLength(answer);
  if (input.previousTurn?.answer.trim() && limited === input.previousTurn.answer.trim()) {
    return limitInterpretationLength(`换个角度，${profile.lens}；${facts}才是这次追问能确认的新重点。`);
  }
  return limited;
}
